import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { GuidedState } from '../../pages/GuidedWalkthroughPage';
import { calcDailyGB } from './Step2Infrastructure';
import { saveEstimate } from '../../api/estimatesApi';

interface Props {
  state: GuidedState;
  onReset: () => void;
}

const SECTOR_LABELS: Record<GuidedState['sector'], string> = {
  nhs: 'NHS / Healthcare',
  fca: 'Financial Services (FCA-regulated)',
  public_sector: 'UK Public Sector',
  private: 'Private Sector / Other',
};

const SIZE_LABELS: Record<GuidedState['organisationSize'], string> = {
  small: 'Small (up to 250 employees)',
  medium: 'Medium (250–2,000 employees)',
  large: 'Large (2,000+ employees)',
};

const SECTOR_COMPLIANCE_NOTE: Record<GuidedState['sector'], string> = {
  nhs: 'This estimate is configured for DSPT compliance. Review CAF Principle C1 for specific logging requirements.',
  fca: 'This estimate includes FCA SYSC 9.1.1R minimum retention requirements.',
  public_sector: 'This estimate follows NCSC Cyber Assessment Framework guidance.',
  private: 'Retention and archiving defaults are set to standard commercial best practice.',
};

function calcMonthly(state: GuidedState) {
  const dailyGB = calcDailyGB(state);

  // Free deductions
  let freeGB = 0;
  if (state.sentinelEnabled) {
    if (state.m365Licences > 0) freeGB += state.m365Licences * 0.005; // 5MB/user/day
    if (state.defenderServersP2) {
      freeGB += (state.windowsServers + state.linuxServers + state.azureVMs) * 0.5;
    }
  }
  const billableGB = Math.max(0, dailyGB - freeGB);

  // Ingestion cost (first 5 GB/day free)
  const ingestCost = billableGB <= 5 ? 0 : (billableGB - 5) * 2.1279 * 30;

  // Sentinel cost
  const sentinelCost = state.sentinelEnabled ? billableGB * 3.975 * 30 : 0;

  // Retention cost
  const freeDays = state.sentinelEnabled ? 90 : 31;
  const billableDays = Math.max(0, state.retentionDays - freeDays);
  const retentionCost = billableGB * 30 * 0.0961 * (billableDays / 30);

  // Archive cost
  const archiveCost = state.archiveYears > 0
    ? billableGB * 30 * 0.0185 * (state.archiveYears / 12) * 12
    : 0;

  const total = ingestCost + sentinelCost + retentionCost + archiveCost;

  return { dailyGB, billableGB, freeGB, ingestCost, sentinelCost, retentionCost, archiveCost, total };
}

function formatMonitoring(state: GuidedState): string {
  const parts: string[] = [];
  if (state.needsSecurity) parts.push('Security');
  if (state.needsCompliance) parts.push('Compliance');
  if (state.needsPerformance) parts.push('Performance');
  if (state.needsApplicationLogs) parts.push('Application');
  return parts.length ? parts.join(', ') : 'None selected';
}

function infra(state: GuidedState): string {
  const parts: string[] = [];
  if (state.windowsServers) parts.push(`${state.windowsServers} Windows servers`);
  if (state.linuxServers) parts.push(`${state.linuxServers} Linux servers`);
  if (state.azureVMs) parts.push(`${state.azureVMs} Azure VMs`);
  if (state.desktops) parts.push(`${state.desktops} desktops`);
  if (state.firewalls) parts.push(`${state.firewalls} firewalls`);
  if (state.sqlDatabases) parts.push(`${state.sqlDatabases} SQL databases`);
  if (state.appServices) parts.push(`${state.appServices} app services`);
  return parts.length ? parts.join(', ') : 'None entered';
}

export default function Step5Results({ state, onReset }: Props) {
  const calc = calcMonthly(state);
  const [shareState, setShareState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [shareUrl, setShareUrl] = useState('');

  const handleShare = async () => {
    setShareState('loading');
    try {
      const inputs = {
        sector: state.sector,
        organisationSize: state.organisationSize,
        windowsServers: state.windowsServers,
        linuxServers: state.linuxServers,
        desktops: state.desktops,
        azureVMs: state.azureVMs,
        firewalls: state.firewalls,
        sqlDatabases: state.sqlDatabases,
        appServices: state.appServices,
        sentinelEnabled: state.sentinelEnabled,
        retentionDays: state.retentionDays,
        archiveYears: state.archiveYears,
        m365Licences: state.m365Licences,
        defenderServersP2: state.defenderServersP2,
      };
      const result = {
        monthly_estimate: calc.total,
        daily_gb: calc.dailyGB,
        breakdown: [
          { label: 'Log Analytics ingestion', value: calc.ingestCost, unit: 'GBP' },
          { label: 'Microsoft Sentinel', value: calc.sentinelCost, unit: 'GBP', isFree: !state.sentinelEnabled },
          { label: 'Retention (beyond free period)', value: calc.retentionCost, unit: 'GBP' },
          { label: 'Archive storage', value: calc.archiveCost, unit: 'GBP' },
        ],
        disclaimer: 'Guided estimate — approximate only. Verify through Azure Pricing Calculator.',
      };
      const { url } = await saveEstimate('log-analytics', inputs, result);
      setShareUrl(url);
      setShareState('success');
    } catch {
      setShareState('error');
    }
  };

  const logAnalyticsParams = new URLSearchParams({
    dailyIngestionGB: calc.dailyGB.toFixed(1),
    sentinelEnabled: state.sentinelEnabled ? 'true' : 'false',
  }).toString();

  return (
    <div>
      <h2 className="text-[22px] font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Your estimated Azure monitoring costs</h2>
      <p className="text-[14px] mb-6" style={{ color: 'var(--text-tertiary)' }}>Based on the information you provided.</p>

      {/* Summary card */}
      <div className="rounded-lg p-4 mb-6 text-[12px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <p className="text-[11px] font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--text-tertiary)' }}>Your configuration</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          <div><span style={{ color: 'var(--text-tertiary)' }}>Sector</span><p style={{ color: 'var(--text-secondary)' }}>{SECTOR_LABELS[state.sector]}</p></div>
          <div><span style={{ color: 'var(--text-tertiary)' }}>Organisation size</span><p style={{ color: 'var(--text-secondary)' }}>{SIZE_LABELS[state.organisationSize]}</p></div>
          <div className="col-span-2"><span style={{ color: 'var(--text-tertiary)' }}>Infrastructure</span><p style={{ color: 'var(--text-secondary)' }}>{infra(state)}</p></div>
          <div><span style={{ color: 'var(--text-tertiary)' }}>Monitoring</span><p style={{ color: 'var(--text-secondary)' }}>{formatMonitoring(state)}</p></div>
          <div><span style={{ color: 'var(--text-tertiary)' }}>Sentinel</span><p style={{ color: state.sentinelEnabled ? '#0078d4' : 'var(--text-tertiary)' }}>{state.sentinelEnabled ? 'Enabled' : 'Disabled'}</p></div>
          <div><span style={{ color: 'var(--text-tertiary)' }}>Retention</span><p style={{ color: 'var(--text-secondary)' }}>{state.retentionDays} days interactive, {state.archiveYears} years archive</p></div>
        </div>
      </div>

      {/* Daily ingestion */}
      <div className="bg-[#0078d4]/10 border border-[#0078d4]/40 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Estimated daily ingestion</p>
          <p className="text-[20px] font-bold font-mono text-[#0078d4]">{calc.dailyGB.toFixed(1)} GB/day</p>
        </div>
        {calc.freeGB > 0 && (
          <p className="text-[11px] text-[#107c10] mt-1">
            Free allowance: {calc.freeGB.toFixed(1)} GB/day · Billable: {calc.billableGB.toFixed(1)} GB/day
          </p>
        )}
      </div>

      {/* Cost breakdown */}
      <div className="rounded-lg mb-4 overflow-hidden" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <div className="px-4 py-2" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
          <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>Monthly cost breakdown</p>
        </div>
        <div>
          {[
            { label: 'Log Analytics ingestion', val: calc.ingestCost },
            { label: 'Microsoft Sentinel', val: calc.sentinelCost, disabled: !state.sentinelEnabled },
            { label: 'Retention (beyond free period)', val: calc.retentionCost },
            { label: 'Archive storage', val: calc.archiveCost, disabled: state.archiveYears === 0 },
          ].map((row) => (
            <div key={row.label} className="flex justify-between items-center px-4 py-2.5">
              <span className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{row.label}</span>
              <span className="text-[12px] font-mono font-semibold" style={{ color: row.disabled ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
                {row.disabled ? '—' : `£${row.val.toFixed(2)}`}
              </span>
            </div>
          ))}
        </div>
        <div className="px-4 py-3 flex justify-between items-center" style={{ borderTop: '1px solid var(--outline-variant)', backgroundColor: 'var(--surface-container-low)' }}>
          <span className="text-[13px] font-bold" style={{ color: 'var(--text-secondary)' }}>Total estimated monthly</span>
          <span className="text-[20px] font-bold font-mono text-[#0078d4]">£{calc.total.toFixed(2)}</span>
        </div>
        <div className="px-4 py-2 flex justify-between items-center" style={{ borderTop: '1px solid var(--outline-variant)' }}>
          <span className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Annual estimate</span>
          <span className="text-[13px] font-semibold font-mono" style={{ color: 'var(--text-tertiary)' }}>£{(calc.total * 12).toFixed(2)}</span>
        </div>
      </div>

      {/* Commitment tier recommendation */}
      <div className={[
        'rounded-lg p-4 mb-4 text-[13px]',
        calc.dailyGB > 90
          ? 'bg-[#107c10]/10 border border-[#107c10]/40'
          : 'bg-[#0078d4]/10 border border-[#0078d4]/20',
      ].join(' ')}>
        {calc.dailyGB > 90 ? (
          <p style={{ color: 'var(--text-secondary)' }}>
            💡 <strong>A commitment tier could save you approximately £{((calc.ingestCost * 0.25)).toFixed(0)}/month</strong>{' '}
            vs Pay-as-you-go at your data volumes.
          </p>
        ) : (
          <p style={{ color: 'var(--text-tertiary)' }}>
            ✓ <strong style={{ color: 'var(--text-secondary)' }}>Pay-as-you-go is the most cost-effective plan</strong>{' '}
            at your data volumes ({calc.dailyGB.toFixed(1)} GB/day).
          </p>
        )}
      </div>

      {/* Compliance note */}
      <div className="rounded-lg p-4 mb-6 text-[12px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}>
        📌 {SECTOR_COMPLIANCE_NOTE[state.sector]}
      </div>

      {/* CTAs */}
      <div className="flex flex-col gap-3">
        <Link
          to={`/log-analytics?${logAnalyticsParams}`}
          className="flex items-center justify-center gap-2 w-full py-3 bg-[#0078d4] hover:bg-[#106ebe] rounded-lg text-white text-[14px] font-semibold transition-colors"
        >
          <span className="material-symbols-outlined text-[18px]">open_in_new</span>
          See full technical breakdown →
        </Link>

        <button
          onClick={handleShare}
          disabled={shareState === 'loading' || shareState === 'success'}
          className={[
            'flex items-center justify-center gap-2 w-full py-3 rounded-lg text-[14px] font-semibold transition-all border',
            shareState === 'success'
              ? 'border-[#107c10] text-[#107c10] bg-[#107c10]/10'
              : 'hover:border-[#0078d4] hover:text-[#0078d4]',
          ].join(' ')}
        >
          <span className="material-symbols-outlined text-[18px]">
            {shareState === 'loading' ? 'hourglass_empty' : shareState === 'success' ? 'check_circle' : 'share'}
          </span>
          {shareState === 'idle' && 'Save & Share this estimate'}
          {shareState === 'loading' && 'Saving…'}
          {shareState === 'success' && 'Saved!'}
          {shareState === 'error' && 'Failed — try again'}
        </button>

        {shareState === 'success' && shareUrl && (
          <p className="text-[11px] text-[#107c10] text-center break-all">{shareUrl}</p>
        )}

        <button
          onClick={onReset}
          className="text-[13px] transition-colors py-2"
          style={{ color: 'var(--text-tertiary)' }}
        >
          Start over
        </button>
      </div>

      <p className="text-[10px] mt-6 leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
        DISCLAIMER: This guided estimate is an approximation only. Prices sourced from the Azure Retail
        Prices API. Actual costs depend on exact data volumes, Microsoft agreement terms, and usage patterns.
        Always verify through the official Azure Pricing Calculator.
      </p>
    </div>
  );
}
